import JSZip from 'jszip'
import { saveAs } from 'file-saver'

export interface ResourceItem {
  resource: string;
  image_id?: string;
  [key: string]: any;
}

export interface DownloadOptions {
  endpoint?: string;
  timeout?: number;
}

export interface DownloadSummary {
  successCount: number;
  failureCount: number;
  failures: Array<{ resource: string; reason: string }>;
}

function getResourceExtension(resource: string): string {
  const getExtensionFromPath = (pathname: string): string => {
    const lastSegment = pathname.split('/').pop() || '';
    const dotIndex = lastSegment.lastIndexOf('.');
    if (dotIndex <= 0 || dotIndex === lastSegment.length - 1) {
      return 'jpg';
    }
    return lastSegment.slice(dotIndex + 1).trim() || 'jpg';
  };

  try {
    const pathname = new URL(resource, window.location.origin).pathname;
    return getExtensionFromPath(pathname);
  } catch {
    const cleanPath = resource.split('?')[0].split('#')[0];
    return getExtensionFromPath(cleanPath);
  }
}

function formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch (_error) {
    return 'Unknown error';
  }
}

async function concurrentTasks<T>(tasks: (() => Promise<T>)[], maxConcurrency: number): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = new Array(tasks.length);
  let nextIndex = 0;
  const workerCount = Math.min(maxConcurrency, tasks.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const currentIndex = nextIndex++;
      if (currentIndex >= tasks.length) {
        return;
      }
      try {
        const value = await tasks[currentIndex]();
        results[currentIndex] = { status: 'fulfilled', value };
      } catch (error) {
        results[currentIndex] = { status: 'rejected', reason: error };
      }
    }
  });

  await Promise.all(workers);
  return results;
}

/**
 * 将资源数据打包成 zip 文件并下载
 * @param data - 包含资源信息的数组
 * @param filename - 下载的 zip 文件名
 * @param options - 配置选项
 * @throws 下载失败时抛出错误
 */
export async function downloadResourcesAsZip(
  data: ResourceItem[],
  filename: string = 'resources.zip',
  options: DownloadOptions = {}
): Promise<DownloadSummary> {
  const {
    endpoint = '/cms/v1/resource',
    timeout = 45000,
  } = options;

  const zip = new JSZip();
  const failures: Array<{ resource: string; reason: string }> = [];

  zip.file('data.json', JSON.stringify(data, null, 2));

  const resourceItems = data
    .map((item, index) => ({
      item,
      index,
      resource: String(item.resource || '').trim(),
    }))
    .filter(({ resource }) => resource);
  const imageTasks: Array<() => Promise<{ fileName: string; blob: Blob }>> = [];
  const MAX_CONCURRENCY = 4;

  resourceItems.forEach(({ item, index, resource }) => {
    imageTasks.push(async () => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeout);
      const downloadUrl = `${endpoint}${endpoint.includes('?') ? '&' : '?'}resource=${encodeURIComponent(resource)}`;

      try {
        const response = await fetch(downloadUrl, {
          signal: controller.signal,
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const fileName = `${item.image_id || item.id || item._id || `未知id-${index}`}.${getResourceExtension(resource)}`;
        return { fileName, blob };
      } catch (error) {
        throw new Error(`resource=${resource}: ${formatError(error)}`);
      } finally {
        clearTimeout(timeoutId);
      }
    });
  });

  const settledResults = await concurrentTasks(imageTasks, MAX_CONCURRENCY);
  const successfulResults = settledResults
    .filter((result): result is PromiseFulfilledResult<{ fileName: string; blob: Blob }> => result.status === 'fulfilled')
    .map(result => result.value);

  settledResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      failures.push({
        resource: resourceItems[index]?.resource || '',
        reason: formatError(result.reason),
      });
    }
  });

  if (successfulResults.length === 0) {
    throw new Error(failures[0]?.reason || '图片下载失败');
  }

  successfulResults.forEach(({ fileName, blob }) => {
    zip.file(fileName, blob);
  });

  if (failures.length > 0) {
    zip.file(
      'failed.txt',
      failures.map((item, index) => `${index + 1}. ${item.resource} -> ${item.reason}`).join('\n')
    );
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, filename);

  return {
    successCount: successfulResults.length,
    failureCount: failures.length,
    failures,
  };
}
